import * as React from 'react';
import '../styles/home.css';
import Modal from '@cloudscape-design/components/modal';
import Box from '@cloudscape-design/components/box';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Button from '@cloudscape-design/components/button';
import FileUpload from '@cloudscape-design/components/file-upload';
import FormField from '@cloudscape-design/components/form-field';
import { useState } from 'react';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import Spinner from '@cloudscape-design/components/spinner';
import { addUsage } from './add-usage.js';
import mammoth from 'mammoth';
import jsPDF from 'jspdf';
import wordIcon from '../assets/word-icon.svg';
import { PDFDocument } from 'pdf-lib';
import constants from '../constants.js';

const DocxToPdf = ({ isBeta }) => {
  const [visible, setVisible] = React.useState(false);
  const [value, setValue] = React.useState([]);
  const [submitvalue, setSubmitvalue] = useState('Convert');
  const [servicevalue, setServicevalue] = useState([]);
  const [notify, setNotify] = useState(null);
  const [error, setError] = useState(null);
  const [fileCount, setFileCount] = useState(0);
  const [totalsize, setTotalsize] = useState(0);

  const toggleModal = React.useCallback(
    (service) => () => {
      setVisible(true);
      setValue('');
      setServicevalue(service);
      setSubmitvalue('Convert');
      setNotify('');
      setError('');
      setFileCount(0);
      setTotalsize(0);
    },
    [],
  );

  const onClickFooterButton = () => {
    setVisible(false);
    setValue('');
    setSubmitvalue('Convert');
    setNotify('');
    setError('');
    setFileCount(0);
    setTotalsize(0);
  };

  async function _handleInputChange(e) {
    setValue(e.detail.value);
    let files = e.detail.value;

    const docxFiles = files.filter(
      (file) => file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    );

    if (docxFiles.length !== files.length) {
      setNotify('Only DOCX files are supported');
      setError('error');
    } else {
      setNotify('');
      setError(null);
      setFileCount(files.length);
    }

    let valueAdded = 0;
    for (const file of docxFiles) {
      valueAdded += file.size;
    }
    setTotalsize(valueAdded / 1000000);
  }

  const convertWordToHtml = async (wordFile) => {
    const arrayBuffer = await wordFile.arrayBuffer();
    const container = document.createElement('div');
    container.className = 'word-document-container';
    container.style.position = 'absolute';
    container.style.left = '0';
    container.style.top = '0';
    container.style.width = '800px';
    container.style.height = 'auto';
    container.style.opacity = '0.01';
    container.style.pointerEvents = 'none';
    document.body.appendChild(container);

    try {
      // Apply basic container styles without overriding colors
      container.style.fontFamily = 'Calibri, Arial, sans-serif';
      container.style.lineHeight = '1.5';
      container.style.margin = '0';
      container.style.padding = '40px';
      container.style.border = 'none';
      container.style.maxWidth = '720px';
      container.style.boxSizing = 'border-box';

      const style = document.createElement('style');
      style.textContent = `
        .word-document-container { background: white; }
        .word-document-container table { border-collapse: collapse; }
        .word-document-container td, .word-document-container th { border: 1px solid #ccc; }
      `;
      document.head.appendChild(style);

      const result = await mammoth.convertToHtml({
        arrayBuffer,
        styleMap: [
          "p[style-name='Heading 1'] => h1:fresh",
          "p[style-name='Heading 2'] => h2:fresh",
          "p[style-name='Heading 3'] => h3:fresh",
          'table => table.preserve-colors',
          'tr => tr.preserve-colors',
          'td => td.preserve-colors',
          'th => th.preserve-colors',
        ],
        includeDefaultStyleMap: true,
        includeEmbeddedStyleMap: true,
        ignoreEmptyParagraphs: false,
        convertImage: mammoth.images.imgElement(function (image) {
          return image.read('base64').then(function (imageBuffer) {
            return {
              src: 'data:' + image.contentType + ';base64,' + imageBuffer,
            };
          });
        }),
      });
      // Check for warnings in the conversion result
      if (result.messages && result.messages.length > 0) {
        console.warn('DOCX conversion warnings:', result.messages);
      }
      container.innerHTML = result.value;

      // Post-process footnotes and endnotes
      const footnotes = container.querySelectorAll('[id^="footnote-"], [id^="endnote-"]');
      footnotes.forEach((note) => {
        note.style.fontSize = '0.9em';
        note.style.borderTop = '1px solid #ccc';
        note.style.paddingTop = '0.5em';
        note.style.marginTop = '1em';
      });

      // Handle wide tables with content-based column widths
      const tables = container.querySelectorAll('table');
      tables.forEach((table) => {
        table.style.borderCollapse = 'collapse';
        table.style.margin = '1em 0';
        table.style.width = '100%';
        table.style.tableLayout = 'auto';

        // Detect header rows using multiple heuristics
        const rows = table.querySelectorAll('tr');
        const headerRows = new Set();

        rows.forEach((row, rowIndex) => {
          const cells = row.querySelectorAll('td, th');
          let isHeader = false;
          if (row.querySelector('th')) {
            isHeader = true;
          }
          // Check if first few rows have bold content
          else if (rowIndex < 2) {
            const boldCells = Array.from(cells).filter(
              (cell) =>
                cell.querySelector('strong, b') ||
                getComputedStyle(cell).fontWeight === 'bold' ||
                getComputedStyle(cell).fontWeight >= '600',
            );
            if (boldCells.length >= cells.length * 0.5) {
              isHeader = true;
            }
          }
          if (isHeader) {
            headerRows.add(rowIndex);
            cells.forEach((cell) => {
              if (!cell.style.backgroundColor) {
                cell.style.backgroundColor = '#f0f0f0';
              }
              if (!cell.style.fontWeight) {
                cell.style.fontWeight = 'bold';
              }
            });
          }
          // Alternate row colors for non-header rows
          else if (rowIndex % 2 === 0) {
            cells.forEach((cell) => {
              if (!cell.style.backgroundColor) {
                cell.style.backgroundColor = '#f9f9f9';
              }
            });
          }
        });
        // Get max column count from all rows
        let maxColumns = 0;
        for (let row of table.rows) {
          maxColumns = Math.max(maxColumns, row.cells.length);
        }
        // Calculate content-based widths with performance optimization
        const MAX_ROWS_TO_SAMPLE = 20;
        const rowsToProcess = Math.min(table.rows.length, MAX_ROWS_TO_SAMPLE);
        const columnWidths = [];

        for (let colIndex = 0; colIndex < maxColumns; colIndex++) {
          let maxContentLength = 0;
          for (let i = 0; i < rowsToProcess; i++) {
            const row = table.rows[i];
            if (row.cells[colIndex]) {
              const content = row.cells[colIndex].textContent || '';
              maxContentLength = Math.max(maxContentLength, content.length);
            }
          }
          columnWidths.push(maxContentLength);
        }
        const totalContentLength = columnWidths.reduce((sum, length) => sum + length, 0);
        const cells = table.querySelectorAll('td, th');

        // Configurable font sizes with original pixel measurements
        const tableConfig = {
          smallTableFontSize: '11px',
          largeTableFontSize: '9px',
          largeTableCellFontSize: '8px',
        };

        // Apply responsive styling based on column count
        if (maxColumns > 5) {
          table.style.fontSize = tableConfig.largeTableFontSize;
          // Assign widths based on content length
          for (let row of table.rows) {
            for (let colIndex = 0; colIndex < row.cells.length; colIndex++) {
              const cell = row.cells[colIndex];
              const contentRatio = columnWidths[colIndex] / totalContentLength;
              const width = Math.max(contentRatio * 100, 100 / maxColumns); // Minimum equal width
              cell.style.border = '1px solid #000';
              cell.style.padding = '2px';
              cell.style.fontSize = tableConfig.largeTableCellFontSize;
              cell.style.wordWrap = 'break-word';
              cell.style.width = `${width}%`;
              cell.style.minWidth = '0';
              cell.style.overflow = 'hidden';
            }
          }
        } else {
          cells.forEach((cell) => {
            cell.style.border = '1px solid #000';
            cell.style.padding = '6px';
            cell.style.fontSize = tableConfig.smallTableFontSize;
          });
        }
      });

      // Process images to maintain proportions but avoid page breaks
      const images = container.querySelectorAll('img');
      images.forEach((img) => {
        img.style.display = 'block';
        img.style.breakInside = 'avoid';
        if (!img.style.maxWidth) {
          img.style.maxWidth = '100%';
        }
        if (!img.style.height) {
          img.style.height = 'auto';
        }
      });
      return container;
    } catch (e) {
      console.error('Error converting docx to html:', e);
      if (container && document.body.contains(container)) {
        document.body.removeChild(container);
      }
      throw new Error('Failed to convert Word document to HTML');
    }
  };

  const convertHtmlToPdf = async (tempContainer) => {
    try {
      // Create temp PDF to analyze split image positions
      const tempPdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
      });

      const tempDoc = await new Promise((resolve) => {
        tempPdf.html(tempContainer, {
          callback: async function (doc) {
            resolve(doc);
          },
          width: 170,
          windowWidth: 720,
          autoPaging: 'text',
          margin: [20, 20],
          html2canvas: {
            backgroundColor: '#ffffff',
            scrollX: 0,
            scrollY: 0,
            x: 0,
            y: 0,
          },
        });
      });

      const pageCount = tempDoc.getNumberOfPages();

      // Find which page each image appears on and detect splits
      const imageIdsPerPage = new Map();

      for (let pageNum = 1; pageNum <= pageCount; pageNum++) {
        tempDoc.setPage(pageNum);
        const pageContent = tempDoc.internal.pages[pageNum];
        const contentStr = pageContent ? pageContent.join('') : '';

        // Extract image IDs from page content - try multiple patterns
        let imageIds = [];

        // Pattern 1: /I# Do format
        const pattern1 = contentStr.match(/\/I(\d+)\s+Do/g) || [];
        imageIds = imageIds.concat(pattern1.map((match) => match.match(/\/I(\d+)/)[1]));
        // Pattern 2: Look for image objects in PDF stream
        const pattern2 = contentStr.match(/obj.*?\/Type\s*\/XObject.*?\/Subtype\s*\/Image/gs) || [];
        imageIds = imageIds.concat(pattern2.map((_, index) => (index + 1).toString()));
        // Remove duplicates
        imageIds = [...new Set(imageIds)];

        if (imageIds.length > 0) {
          imageIdsPerPage.set(pageNum, imageIds);
        }
      }
      // Detect split images by finding same image ID on consecutive pages
      const splitImages = new Map();
      for (let pageNum = 1; pageNum < pageCount; pageNum++) {
        const currentPageIds = imageIdsPerPage.get(pageNum) || [];
        const nextPageIds = imageIdsPerPage.get(pageNum + 1) || [];

        // Check for common image IDs between consecutive pages
        const commonIds = currentPageIds.filter((id) => nextPageIds.includes(id));
        if (commonIds.length > 0) {
          commonIds.forEach((id) => {
            splitImages.set(id, pageNum);
          });
        }
      }

      // Hide split images from HTML
      const images = tempContainer.querySelectorAll('img');
      const splitImageData = [];
      images.forEach((img, index) => {
        const imageId = index.toString();
        if (splitImages.has(imageId)) {
          splitImageData.push({
            src: img.src,
            originalPage: splitImages.get(imageId),
          });
          img.style.visibility = 'hidden';
        }
      });
      return new Promise((resolve) => {
        const pdf = new jsPDF({
          orientation: 'p',
          unit: 'mm',
          format: 'a4',
          compress: true,
        });
        pdf.html(tempContainer, {
          callback: async function (doc) {
            const originalPageCount = doc.getNumberOfPages();

            // Add split images on new pages
            splitImageData.forEach((imgData) => {
              doc.addPage();
              doc.addImage(imgData.src, 'JPEG', 20, 30, 170, 170);
            });

            // Recalculate split image page numbers after adding
            const updatedSplitImagePages = [];
            splitImageData.forEach((imgData, index) => {
              const newPageNum = originalPageCount + index + 1;
              updatedSplitImagePages.push(newPageNum);
            });

            // Move content pages based on updated split image positions
            splitImageData.forEach((imgData, index) => {
              const updatedImagePageNum = updatedSplitImagePages[index];
              const startPage = imgData.originalPage + 1;
              const endPage = updatedImagePageNum - 1;

              for (let p = startPage; p <= endPage; p++) {
                doc = doc.movePage(startPage, updatedImagePageNum);
              }

              // Find proper page number of split image ID after moving
              const splitImageId = [...splitImages.keys()][index + 1];
              let newImagePageNum = null;
              for (let pageNum = 1; pageNum <= doc.getNumberOfPages(); pageNum++) {
                doc.setPage(pageNum);
                const pageContent = doc.internal.pages[pageNum]?.join('') || '';
                if (pageContent.includes(`/I${parseInt(splitImageId) + 1}`)) {
                  newImagePageNum = pageNum;
                  break;
                }
              }

              if (newImagePageNum) {
                updatedSplitImagePages[index] = newImagePageNum;
                splitImageData[index + 1].originalPage = newImagePageNum;
              }
            });

            const pdfBytes = doc.output('arraybuffer');
            const pdfDoc = await PDFDocument.load(pdfBytes);
            resolve(pdfDoc);
          },
          width: 170,
          windowWidth: 720,
          autoPaging: 'text',
          margin: [20, 20],
          html2canvas: {
            backgroundColor: '#ffffff',
            scrollX: 0,
            scrollY: 0,
            x: 0,
            y: 0,
          },
        });
      });
    } finally {
      if (tempContainer && document.body.contains(tempContainer)) {
        document.body.removeChild(tempContainer);
      }
    }
  };

  async function Converting() {
    setSubmitvalue(<Spinner />);

    if (!value || value.length === 0) {
      setNotify('Please select DOCX files');
      setError('error');
      setSubmitvalue('Convert');
      return;
    }

    try {
      const pdfDoc = await PDFDocument.create();

      for (const file of value) {
        const wordHtml = await convertWordToHtml(file);
        const wordPdf = await convertHtmlToPdf(wordHtml);
        const copiedPages = await pdfDoc.copyPages(wordPdf, wordPdf.getPageIndices());
        copiedPages.forEach((page) => pdfDoc.addPage(page));
      }

      const mergedPdfBytes = await pdfDoc.save();
      const mergedPdfBlob = new Blob([mergedPdfBytes], { type: constants.pdfFileType });
      const url = URL.createObjectURL(mergedPdfBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'awesomepdf_word_to_pdf.pdf';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      addUsage('AWESOME PDF TOOLS', '1.0', fileCount, window.location.href);

      setSubmitvalue('Success');
      setVisible(false);
    } catch (error) {
      console.error('Error converting Word to PDF:', error);
      setNotify('Error converting files');
      setError('error');
      setSubmitvalue('Convert');
    }
  }

  return (
    <div>
      <div className="service-card" onClick={toggleModal('Word to PDF')}>
        {isBeta && <span className="badge badge-beta">BETA</span>}
        <div className="service-card-icon">
          <img src={wordIcon} alt="Word to PDF" loading="lazy" />
        </div>
        <h3 className="service-card-title">Word to PDF</h3>
        <p className="service-card-desc">Convert Word documents to PDF format</p>
        <div className="service-card-action">
          <span>Get Started</span>
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z" />
          </svg>
        </div>
      </div>

      <Modal
        onDismiss={() => {
          setVisible(false);
        }}
        visible={visible}
        size="small"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={onClickFooterButton}>
                Cancel
              </Button>
              <div>
                {totalsize < 2000 && totalsize > 0 && value.length > 0 ? (
                  <Button variant="primary" onClick={Converting}>
                    {submitvalue}
                  </Button>
                ) : (
                  <Button disabled variant="primary">
                    {totalsize > 2000 ? 'Limit Exceeded' : submitvalue}
                  </Button>
                )}
              </div>
            </SpaceBetween>
          </Box>
        }
        header={servicevalue}
      >
        {servicevalue === 'Word to PDF' ? (
          <div>
            <FormField errorText={notify} label="Upload Word files" description="Only DOCX format is supported">
              <FileUpload
                onChange={_handleInputChange}
                value={value}
                i18nStrings={{
                  uploadButtonText: (e) => (e ? 'Choose files' : 'Choose file'),
                  dropzoneText: (e) => (e ? 'Drop files to upload' : 'Drop file to upload'),
                  removeFileAriaLabel: (e) => `Remove file ${e + 1}`,
                  limitShowFewer: 'Show fewer files',
                  limitShowMore: 'Show more files',
                  errorIconAriaLabel: 'Error',
                }}
                multiple
                showFileLastModified={false}
                showFileSize={false}
                showFileThumbnail={false}
                tokenLimit={9}
                constraintText=""
                accept=".docx"
              />
            </FormField>
          </div>
        ) : (
          <div></div>
        )}
        <br />
        Current size: {totalsize.toFixed(2)}
        <span>MB</span>
        <br />
        Total maximum limit: 2GB
      </Modal>
    </div>
  );
};

export default DocxToPdf;
